import { ElementRef, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AccountAdminCountriesEntity, AccountAdminStateEntity } from '../models/account-admin-search';
import { HttpClient } from '@angular/common/http';
import { ApiOptionsService } from 'common/services/api-options/api-options.service';
import { Option } from 'common/components/select/select.component';
import { map } from 'rxjs/operators';
import { environment } from 'common/environments/environment';
import { CacheNetworkObservableResponse } from '../models/Cache-Observable-Response';
import { DisabledDropdownStrings, Values } from './account-admin/account-admin-form-group-config';
import { BrandsOptions, OptGroupOptions } from '../models/distributor-relationships.model';
import { Brand, Country } from './user-validation/user-validation.service';
import { TranslateService } from '@ngx-translate/core';
import { Validators } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class CommonUtilsService {
    public dropDownsList: string[] = [DisabledDropdownStrings.contactType, DisabledDropdownStrings.rsm, DisabledDropdownStrings.tsm, DisabledDropdownStrings.pricingAnal, DisabledDropdownStrings.accountManager];

    public disabledDropdowns: Values = {
        contactType: false,
        rsm: false,
        tsm: false,
        accountManager: false,
        pricingAnal: false
    }

    private cacheForStateEntity: CacheNetworkObservableResponse<AccountAdminStateEntity[]>;
    private cacheForCountryEntity: CacheNetworkObservableResponse<AccountAdminCountriesEntity[]>;

    constructor(
        private translate: TranslateService,
        private readonly httpClient: HttpClient,
        private readonly apiOptions: ApiOptionsService
    ) {}

    public smoothScrollToTop(element: ElementRef) {
        element.nativeElement.parentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        });
    }

    public getStateByCountry(selectedCountry: string): Observable<Option[]> {
        if (selectedCountry === '') {
            return of([]);
        }

        return this.getStateEntityByCountry(selectedCountry).pipe(
            map((stateEntityList: AccountAdminStateEntity[]): Option[] => {
                const optionList = stateEntityList.map((stateEntity: AccountAdminStateEntity): Option => ({
                    name: stateEntity.name,
                    value: stateEntity.code
                }));

                return optionList;
            })
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public reprioritizeCountries(options: any) {
        const filteredOptions = options.filter((item: Country) => item.value !== 'MX' && item.value !== 'US' && item.value !== 'CA' && item.value !== '');
        filteredOptions.unshift({
            name: 'Mexico',
            value: 'MX'
        });
        filteredOptions.unshift({
            name: 'Canada',
            value: 'CA'
        });
        filteredOptions.unshift({
            name: 'USA',
            value: 'US'
        });
        filteredOptions.unshift({
            name: this.translate.instant('ACCOUNT_ADMIN.SELECT_COUNTRY'),
            value: ''
        });

        return filteredOptions;
    }

    public getStateEntityByCountry(selectedCountry: string): Observable<AccountAdminStateEntity[]> {
        if (selectedCountry === '') {
            return of([]);
        }

        const urlForSelectedCountry = environment.api.getStateListByCountry.split('{{state}}').join(selectedCountry);

        if (!this.cacheForStateEntity) {
            this.cacheForStateEntity = CacheNetworkObservableResponse.createCachedClass<AccountAdminStateEntity[]>(
                this.cacheForStateEntity,
                this.httpClient,
                this.apiOptions,
                this.getStateEntityByCountry.name
            );
        }

        return this.cacheForStateEntity.value(urlForSelectedCountry);
    }

    public getCountryOptions(): Observable<Option[]> {
        return this.getCountriesEntity().pipe(
            map((countryEntities: AccountAdminCountriesEntity[]) => countryEntities.map((countryEntity: AccountAdminCountriesEntity) => ({
                name: countryEntity.name,
                value: countryEntity.code
            })))
        );
    }

    public getCountriesEntity(): Observable<AccountAdminCountriesEntity[]> {
        if (!this.cacheForCountryEntity) {
            this.cacheForCountryEntity = CacheNetworkObservableResponse.createCachedClass<AccountAdminCountriesEntity[]>(
                this.cacheForCountryEntity,
                this.httpClient,
                this.apiOptions,
                this.getCountriesEntity.name
            );
        }

        return this.cacheForCountryEntity.value(environment.api.getCountries);
    }

    public updateDropdownState(field: keyof Values) {
        const updatedValue: Values = {
            contactType: field !== DisabledDropdownStrings.contactType,
            rsm: field !== DisabledDropdownStrings.rsm,
            tsm: field !== DisabledDropdownStrings.tsm,
            accountManager: field !== DisabledDropdownStrings.accountManager,
            pricingAnal: field !== DisabledDropdownStrings.pricingAnal
        };

        return updatedValue;
    }

    public enableAllDropdowns() {
        const updatedValue: Values = {
            contactType: false,
            rsm: false,
            tsm: false,
            accountManager: false,
            pricingAnal: false
        };

        return updatedValue;
    }

    public transformBrandsToGroups(brands?: BrandsOptions[]): OptGroupOptions[] {
        const brandGroups: OptGroupOptions[] = [{
            parent: this.translate.instant('ACCOUNT_ADMIN.SELECT_BRAND'),
            children: []
        }];
        const filteredBrands = brands?.filter((item: Brand) => (item.usageType && item.usageType.includes('PRODUCTS')) || item.name === this.translate.instant('ACCOUNT_ADMIN.SELECT_BRAND'));

        filteredBrands?.forEach((brand) => {
            if (brand.catalogBrandOf) {
                let parentGroup = brandGroups.find((group) => group.parent === brand?.catalogBrandOf?.name);

                if (!parentGroup) {
                    parentGroup = {
                        parent: brand.catalogBrandOf.name,
                        children: []
                    };
                    brandGroups.push(parentGroup);
                }

                parentGroup.children.push({
                    name: brand.name,
                    value: brand.value
                });
            }
            else {
                brandGroups.find((group) => group.parent === 'Select Brand')?.children.push({
                    name: brand.name,
                    value: brand.value
                });
            }
        });

        const nameCount = new Map<string, number>();
        brandGroups.forEach((group) => {
            group.children.forEach((child) => {
                nameCount.set(child.name, (nameCount.get(child.name) || 0) + 1);
            });
        });

        brandGroups.forEach((group) => {
            group.children = group.children.map((child) => ({
                name: nameCount.get(child.name)! > 1 ? `${child.name} - ${child.value}` : child.name,
                value: child.value
            }));
        });

        const sortedbrandGroups = brandGroups.sort((valueA, valueB) => valueA.parent.toLowerCase().localeCompare(valueB.parent.toLowerCase()));
        const index = sortedbrandGroups.findIndex((item) => item.parent === 'Select Brand');

        if (index !== -1) {
            const [SelectBrands] = sortedbrandGroups.splice(index, 1);
            sortedbrandGroups.unshift(SelectBrands);
        }

        return sortedbrandGroups;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updatePhoneValidators(form: any, countryCode: string, phoneControls: string[]) {
        let phonePattern: RegExp;
        let maxLength: number;
        if (countryCode === 'US' || countryCode === 'CA') {
            phonePattern = /^(\+1\s)?\([0-9]{3}\)\s[0-9]{3}[-\s][0-9]{4}$/;
            maxLength = 20;
        }
        else {
            phonePattern = /^[0-9+()\s-]{1,20}$/;
            maxLength = 20;
        }
        phoneControls.forEach((controlName) => {
            const control = form.controls[controlName];
            if (control) {
                control.clearValidators();
                control.setValidators([Validators.maxLength(maxLength), Validators.pattern(phonePattern)]);
                control.updateValueAndValidity();
            }
        });
    }

    formatPhoneNumber(value: string, countryCode: string): string {
        if (countryCode === 'US' || countryCode === 'CA') {
            const cleanValue = value.replace(/-/g, '');
            if (cleanValue.startsWith('+1')) {
                const digits = cleanValue.replace(/[^0-9]/g, '').substring(1);
                if (digits.length === 10) {
                    return `+1 (${digits.substring(0, 3)}) ${digits.substring(3, 6)} ${digits.substring(6)}`;
                }
            }
            else {
                const digits = cleanValue.replace(/[^0-9]/g, '');
                if (digits.length === 10) {
                    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)} ${digits.substring(6)}`;
                }
            }

            return cleanValue;
        }

        return value.replace(/[^0-9+()\s-]/g, '').substring(0, 20);
    }

    getPhonePlaceholder(fieldType: string, countryCode: string, formType?: string): string {
        if (countryCode === 'US' || countryCode === 'CA') {
            return '(123) 456 7890';
        }
        if (formType === 'userInfo') {
            return fieldType === 'Phone' ? 'Phone' : fieldType === 'Cell' ? 'Cell Phone' : 'Fax Number';
        }

        return fieldType === 'office' ? 'Office Phone' : fieldType === 'mobile' ? 'Mobile Number' : 'Fax Number';
    }

    formatPostalCode(value: string, countryCode: string): string {
        if (!value) {
            return value;
        }

        const code = countryCode?.toUpperCase();
        if (code === 'US' || code === 'USA') {
            const cleanValue = value.replace(/\D/g, '');

            if (cleanValue.length <= 5) {
                return cleanValue;
            }

            return `${cleanValue.substring(0, 5)}-${cleanValue.substring(5)}`;
        }
        if (code === 'CA' || code === 'CANADA') {
            const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().substring(0, 6);

            if (cleanValue.length <= 3) {
                return cleanValue;
            }

            return `${cleanValue.substring(0, 3)} ${cleanValue.substring(3)}`;
        }

        return value;
    }
}
